import { Camera, KeyRound, Sparkles, Star } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Brand marks for the Connected Apps directory.
 *
 * The artwork sits directly on the card — no plate, no border, nothing framing
 * it. Every mark is fitted to the same 32px box with `object-contain`, so a
 * wide mark and a tall mark still share one optical weight and one baseline
 * down the grid. That single shared box is the only thing enforcing alignment,
 * so nothing here is allowed to set its own size.
 *
 * Dropping the plate means each mark has to survive on both a light and a dark
 * card, which the artwork was chosen to do:
 *
 *   • quickbooks.svg — green disc with the "qb" knocked out to white. Carries
 *     its own ground, so it reads anywhere. `fill-rule="evenodd"` is load
 *     bearing: under the default nonzero rule the counters fill and you get a
 *     solid green blob.
 *   • zapier.svg     — the 150px app icon off zapier.com/brand; an orange
 *     plate of its own, so it also needs no help.
 *   • stripe.svg     — stripe.com's own favicon.svg: their #533AFD app tile.
 *     Stripe's published logo kit holds only a wordmark, which can't sit in a
 *     square box next to seven square marks, so the app tile is the mark.
 *   • wisetack.png   — wisetack.com apple-touch-icon. Measured 87.5% mint
 *     #07C0CA with white fold lines and no black, so the origami bird holds up
 *     on a dark card unaided.
 *   • payabli.svg    — the mark lifted out of the lockup on payabli.com. This
 *     is the one drawn in near-black (#020B27), so it's the one mark that gets
 *     flipped to white in dark mode.
 *
 * Serving them from our own `public/` keeps the page free of third-party asset
 * requests, so nothing breaks behind a corporate proxy.
 *
 * Review platforms, Iris and the Partner API aren't third-party products —
 * they're Notifi surfaces — so they take a design-system glyph, not a logo.
 *
 * Marks are used nominatively, to identify the service each card connects to.
 */

export type BrandLogoId =
  | 'quickbooks'
  | 'stripe'
  | 'payabli'
  | 'wisetack'
  | 'reviews'
  | 'iris'
  | 'zapier'
  | 'companycam'
  | 'partnerApi';

interface BrandMark {
  label: string;
  /** Vendor artwork under public/brand/integrations/. */
  src?: string;
  /** Design-system glyph, for the Notifi surfaces that have no vendor logo. */
  icon?: LucideIcon;
  iconClassName?: string;
  /** Marks that carry their own coloured plate want a soft corner radius. */
  rounded?: boolean;
  /**
   * Set for near-black artwork only. `brightness-0 invert` flattens the mark
   * to pure white in dark mode rather than leaving it to sink into the card.
   */
  invertInDark?: boolean;
}

const MARKS: Record<BrandLogoId, BrandMark> = {
  quickbooks: { label: 'QuickBooks', src: '/brand/integrations/quickbooks.svg' },
  stripe: { label: 'Stripe', src: '/brand/integrations/stripe.svg', rounded: true },
  payabli: { label: 'Payabli', src: '/brand/integrations/payabli.svg', invertInDark: true },
  wisetack: { label: 'Wisetack', src: '/brand/integrations/wisetack.png' },
  zapier: { label: 'Zapier', src: '/brand/integrations/zapier.svg', rounded: true },
  reviews: {
    label: 'Review platforms',
    icon: Star,
    iconClassName: 'fill-amber-400 text-amber-500',
  },
  iris: { label: 'Iris', icon: Sparkles, iconClassName: 'text-brand-blue' },
  // No vendor artwork on hand; a glyph beats a wrong-colour approximation.
  companycam: {
    label: 'CompanyCam',
    icon: Camera,
    iconClassName: 'text-sky-600',
  },
  partnerApi: { label: 'Partner API', icon: KeyRound, iconClassName: 'text-slate-500' },
};

export function BrandLogo({ id, className }: { id: BrandLogoId; className?: string }) {
  const mark = MARKS[id];
  const Icon = mark.icon;

  return (
    <span
      data-testid={`brand-logo-${id}`}
      className={cn('flex size-8 shrink-0 items-center justify-center', className)}
    >
      {mark.src ? (
        <img
          src={mark.src}
          alt={mark.label}
          loading="lazy"
          decoding="async"
          className={cn(
            'size-8 object-contain',
            mark.rounded && 'rounded-[7px]',
            mark.invertInDark && 'dark:brightness-0 dark:invert',
          )}
        />
      ) : Icon ? (
        <Icon className={cn('size-7', mark.iconClassName)} aria-label={mark.label} />
      ) : null}
    </span>
  );
}
